import React, { Component } from "react";
import { Select, TreeSelect } from "antd";
import RefinateFiltersFormHolder from "../styles/refinateFiltersFormStyle";
import RefinateFiltersMapView from "./RefinateFiltersMapView";
import RefinateFiltersTableView from "./RefinateFiltersTableView";
import { debounce, groupBy, keyBy, uniq } from "lodash";
import { getMinimunByCurrency } from "../../../../services/utils";
import { getScreenTypeName } from "../../../../services/display";
import { getVenueName, getVisibleVenueIds, pruneVenueSelection } from "../../../../services/venueCategories";
import Colors from "../../../../themes/Colors";
import { withTranslation } from "react-i18next";

const { Option } = Select;

// Filter state fields that, when changed, re-run the live apply (table view).
const WATCHED_FILTER_KEYS = [
  "localLocationType",
  "localVenueSelection",
  "localSizeType",
  "localIsCircuit",
  "resolutionSelect",
  "customResolution",
  "resolutionTolerance",
  "enableAspectRatio",
  "selectedFormats",
  "durationRange",
  "priceStrategy",
  "priceMin",
  "priceMax",
  "selectedMediaCompanies",
  "selectedPois",
];

const COMMON_RESOLUTIONS = [
  { w: 1080, h: 1920 },
  { w: 1920, h: 1080 },
  { w: 1280, h: 960 },
  { w: 1400, h: 400 },
  { w: 5760, h: 1080 },
  { w: 840, h: 400 },
  { w: 192, h: 64 },
  { w: 2160, h: 3840 },
  { w: 1920, h: 896 },
  { w: 1280, h: 720 },
  { w: 1366, h: 768 },
  { w: 1320, h: 240 },
  { w: 720, h: 1280 },
  { w: 400, h: 600 },
  { w: 930, h: 1600 },
  { w: 612, h: 306 },
  { w: 224, h: 336 },
  { w: 1080, h: 1830 },
  { w: 448, h: 640 },
  { w: 320, h: 512 },
];

/**
 * Controller for the refine-search filters. Owns all state, handlers and the
 * apply/clear logic. Rendering is delegated to a presentational view chosen by
 * the `variant` prop:
 *   - "table" → RefinateFiltersTableView (card layout + points of interest)
 *   - "map"   → RefinateFiltersMapView (original modal layout)
 */
class RefinateFiltersForm extends Component {
  constructor(props) {
    super(props);
    const initial = props.initialValues || {};

    let initialSelectedCompanies = [];
    if (initial.company_list_ids) {
      initialSelectedCompanies = initial.company_list_ids
        .split(",")
        .map(Number);
    }

    this.state = {
      localLocationType: [...(props.location_type || [])],
      // Category filter selection: root ids (categories) and subcategory ids, as in the URL,
      // without what the types hide (a link can bring subcategories of another type).
      localVenueSelection: pruneVenueSelection(
        [...(props.location_sub_type || [])].map(Number),
        props.venueTypes,
        props.location_type || [],
      ),
      localSizeType: initial.size_type || [],
      localIsCircuit: !!initial.isCircuit,
      resolutionSelect: initial.resolutionSelect || [],
      customResolution: initial.customResolution || "",
      resolutionTolerance: initial.resolution_tolerance || 0,
      enableAspectRatio: initial.enable_aspect_ratio_filter || false,
      selectedFormats: initial.allowed_formats || [],
      durationRange: [
        initial.duration_min ? initial.duration_min / 1000 : 5,
        initial.duration_max ? initial.duration_max / 1000 : 50,
      ],
      priceStrategy:
        initial.price_filter_strategy ||
        (props.programmatic === "traditional" ? "day" : "cpm_impressions"),
      priceMin: initial.price_min || "",
      priceMax: initial.price_max || "",
      selectedMediaCompanies: initialSelectedCompanies,
      selectedPois: initial.custom_poi_type || [],
    };

    // Table view applies filters live on every change (no "Apply" button),
    // debounced so rapid typing/sliding coalesces into a single request.
    this.debouncedApply = debounce(this.handleApply, 400);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.programmatic !== this.props.programmatic) {
      this.setState({
        priceStrategy:
          this.props.programmatic === "traditional" ? "day" : "cpm_impressions",
      });
    }

    // Live apply for the table view: fire whenever a filter value changes.
    if (this.props.variant === "table") {
      const changed = WATCHED_FILTER_KEYS.some(
        (key) => prevState[key] !== this.state[key],
      );
      if (changed) {
        this.debouncedApply();
      }
    }
  }

  componentWillUnmount() {
    this.debouncedApply.cancel();
  }

  // A new type drops the categories and subcategories it hides.
  handleLocationTypeChange = (types) => {
    this.setState(({ localVenueSelection }) => ({
      localLocationType: types,
      localVenueSelection: pruneVenueSelection(localVenueSelection, this.props.venueTypes, types),
    }));
  };

  // Root ids of the selection: categories picked directly or through a subcategory.
  getSelectedVenueTypes = (selection) => {
    const byId = keyBy(this.props.venueTypes, "id");
    return uniq(selection.filter((id) => byId[id]).map((id) => byId[id].root_id));
  };

  // Removing a category drops its subcategories too.
  handleTypeChange = (rootIds) => {
    const byId = keyBy(this.props.venueTypes, "id");
    this.setState(({ localVenueSelection }) => ({
      localVenueSelection: [
        ...rootIds,
        ...localVenueSelection.filter(
          (id) => byId[id] && byId[id].level > 1 && rootIds.includes(byId[id].root_id),
        ),
      ],
    }));
  };

  handleSubTypeChange = (subtypeIds) => {
    this.setState(({ localVenueSelection }) => ({
      localVenueSelection: [
        ...this.getSelectedVenueTypes(localVenueSelection),
        ...subtypeIds.map(Number),
      ],
    }));
  };

  handleSizeChange = (size) => {
    const { localSizeType } = this.state;
    if (localSizeType.includes(size)) {
      this.setState({ localSizeType: localSizeType.filter((t) => t !== size) });
    } else {
      this.setState({ localSizeType: [...localSizeType, size] });
    }
  };

  handleCircuitChange = (checked) => {
    this.setState({ localIsCircuit: checked });
  };

  handleResolutionSelect = (val) => {
    this.setState({ resolutionSelect: val });
  };

  handleCustomResolution = (e) => {
    this.setState({
      customResolution: e.target.value,
    });
  };

  handleResolutionTolerance = (val) => {
    this.setState({ resolutionTolerance: val });
  };

  handleAspectRatioChange = (e) => {
    this.setState({ enableAspectRatio: e.target.checked });
  };

  handleFormatChange = (checkedValues) => {
    this.setState({ selectedFormats: checkedValues });
  };

  handleDurationChange = (value) => {
    this.setState({ durationRange: value });
  };

  handlePriceChange = (field, value) => {
    const reg = /^-?\d*(\.\d*)?$/;
    if (reg.test(value) || value === "") {
      this.setState({ [field]: value });
    }
  };

  getPriceSliderMax = () => getMinimunByCurrency(this.props.currency) * 10;

  // Right handle at the slider max means "no limit" (price_max not sent).
  handlePriceSliderChange = (val) => {
    this.setState({
      priceMin: val[0],
      priceMax: val[1] >= this.getPriceSliderMax() ? "" : val[1],
    });
  };

  handleMediaCompanyChange = (values) => {
    this.setState({ selectedMediaCompanies: values });
  };

  handlePoiToggle = (slug) => {
    const { selectedPois } = this.state;
    if (selectedPois.includes(slug)) {
      this.setState({ selectedPois: selectedPois.filter((s) => s !== slug) });
    } else {
      this.setState({ selectedPois: [...selectedPois, slug] });
    }
  };

  handleClear = () => {
    this.props.onSearchChange("");
    this.setState({
      localLocationType: [],
      localVenueSelection: [],
      localSizeType: [],
      localIsCircuit: false,
      resolutionSelect: [],
      customResolution: "",
      resolutionTolerance: 0,
      enableAspectRatio: false,
      selectedFormats: [],
      durationRange: [5, 50],
      priceMin: "",
      priceMax: "",
      selectedMediaCompanies: [],
      selectedPois: [],
      priceStrategy:
        this.props.programmatic === "traditional" ? "day" : "cpm_impressions",
    });
  };

  handleApply = () => {
    const {
      localLocationType,
      localVenueSelection,
      localSizeType,
      localIsCircuit,
      resolutionSelect,
      customResolution,
      resolutionTolerance,
      enableAspectRatio,
      selectedFormats,
      durationRange,
      priceStrategy,
      priceMin,
      priceMax,
      selectedMediaCompanies,
      selectedPois,
    } = this.state;

    this.props.setLocationType(localLocationType);
    this.props.setLocationSubType(localVenueSelection);
    this.props.setSizeType(localSizeType);

    if (this.props.setCircuit) {
      this.props.setCircuit(localIsCircuit ? 1 : 0);
    }

    const filters = {};

    filters.resolutionSelect = resolutionSelect;
    filters.customResolution = customResolution;
    filters.enable_aspect_ratio_filter = enableAspectRatio;
    if (enableAspectRatio) filters.resolution_tolerance = resolutionTolerance || 0;

    let width = null;
    let height = null;
    const resString =
      customResolution ||
      (resolutionSelect.length > 0 ? resolutionSelect[0] : "");
    if (resString && !resString.includes(",")) {
      const parts = resString
        .toLowerCase()
        .split("x")
        .map((p) => p.trim());
      if (parts.length === 2) {
        width = parseInt(parts[0]);
        height = parseInt(parts[1]);
      }
    }
    if (width && height && !isNaN(width) && !isNaN(height)) {
      filters.resolution_width = width;
      filters.resolution_height = height;
    }

    if (selectedFormats.length > 0) {
      filters.allowed_formats = selectedFormats;
    }

    filters.duration_min = durationRange[0] * 1000;
    filters.duration_max = durationRange[1] * 1000;

    if (priceMin !== "" || priceMax !== "") {
      filters.price_filter_strategy = priceStrategy;
      if (priceMin !== "") filters.price_min = parseFloat(priceMin);
      if (priceMax !== "") filters.price_max = parseFloat(priceMax);
    }

    if (selectedMediaCompanies.length > 0) {
      filters.company_list_ids = selectedMediaCompanies.join(",");
    }

    // POI: merged over the map request's custom_poi_type in refreshDisplays.
    if (selectedPois.length > 0) {
      filters.custom_poi_type = selectedPois;
    }

    this.props.onApply(filters);
  };

  // Category (taxonomy roots) and subcategory (tree of their descendants) controls,
  // built once so the map and table views show the same trees. Without
  // taxonomy both are disabled with a notice. The picked types narrow both:
  // every category is listed, the ones without subcategories of the types
  // disabled with a notice, and the tree shows only what the types allow.
  renderVenueFilters = () => {
    const { t, venueTypes } = this.props;
    const { localVenueSelection, localLocationType } = this.state;
    const disabled = !venueTypes.length;
    const byId = keyBy(venueTypes, "id");
    const visible = getVisibleVenueIds(venueTypes, localLocationType);
    const children = groupBy(
      venueTypes.filter((node) => visible.has(node.id)),
      "parent_id",
    );
    const selectedTypes = this.getSelectedVenueTypes(localVenueSelection);
    const toTreeNode = (node) => ({
      title: getVenueName(node),
      value: String(node.id),
      key: String(node.id),
      children: (children[node.id] || []).map(toTreeNode),
    });

    return {
      venueTypeSelect: (
        <>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder={t("Select")}
            onChange={this.handleTypeChange}
            value={disabled ? [] : selectedTypes}
            maxTagCount={1}
            optionFilterProp="children"
            disabled={disabled}
            dropdownStyle={{ borderRadius: "6px" }}
          >
            {venueTypes
              .filter((node) => node.level === 1)
              .map((node) => (
                <Option key={node.id} value={node.id} disabled={!visible.has(node.id)}>
                  {visible.has(node.id)
                    ? getVenueName(node)
                    : `${getVenueName(node)} · ${t("No subcategories of the selected types")}`}
                </Option>
              ))}
          </Select>
          {disabled && (
            <div style={{ color: Colors.textMuted, fontSize: 12, marginTop: 4 }}>
              {t("Screen categories are not available right now")}
            </div>
          )}
        </>
      ),
      venueSubtypeSelect: (
        <TreeSelect
          treeCheckable
          showCheckedStrategy={TreeSelect.SHOW_PARENT}
          treeNodeFilterProp="title"
          treeData={venueTypes
            .filter((node) => node.level === 1 && selectedTypes.includes(node.id))
            .flatMap((root) => (children[root.id] || []).map(toTreeNode))}
          value={
            disabled
              ? []
              : localVenueSelection
                  .filter((id) => byId[id] && byId[id].level > 1 && visible.has(id))
                  .map(String)
          }
          onChange={this.handleSubTypeChange}
          placeholder={t("Select")}
          maxTagCount={1}
          disabled={disabled}
          style={{ width: "100%" }}
          dropdownStyle={{ maxHeight: 400, overflow: "auto", borderRadius: "6px" }}
        />
      ),
    };
  };

  // Everything the presentational views need: derived options, current state
  // values and the bound handlers.
  getViewProps = () => {
    const {
      t,
      programmatic,
      currency,
      displaysMapMediaOwners,
      search,
      onSearchChange,
    } = this.props;
    return {
      t,
      programmatic,
      currency,
      displaysMapMediaOwners,
      commonResolutions: COMMON_RESOLUTIONS,
      priceSliderMax: this.getPriceSliderMax(),
      ...this.renderVenueFilters(),
      // "Type" filter: legacy location_type values
      locationTypeOptions: ["outdoor", "indoor", "buses", "pos"].map((value) => (
        <Option key={value} value={value}>
          {getScreenTypeName(value, t)}
        </Option>
      )),
      onLocationTypeChange: this.handleLocationTypeChange,
      ...this.state,
      search,
      onSearchChange,
      onSizeChange: this.handleSizeChange,
      onCircuitChange: this.handleCircuitChange,
      onResolutionSelect: this.handleResolutionSelect,
      onCustomResolution: this.handleCustomResolution,
      onResolutionTolerance: this.handleResolutionTolerance,
      onAspectRatioChange: this.handleAspectRatioChange,
      onFormatChange: this.handleFormatChange,
      onDurationChange: this.handleDurationChange,
      onPriceChange: this.handlePriceChange,
      onPriceSliderChange: this.handlePriceSliderChange,
      onMediaCompanyChange: this.handleMediaCompanyChange,
      onPoiToggle: this.handlePoiToggle,
      onClear: this.handleClear,
      onApply: this.handleApply,
    };
  };

  render() {
    const isTable = this.props.variant === "table";
    const viewProps = this.getViewProps();

    return (
      <RefinateFiltersFormHolder>
        {isTable ? (
          <RefinateFiltersTableView {...viewProps} />
        ) : (
          <RefinateFiltersMapView {...viewProps} />
        )}
      </RefinateFiltersFormHolder>
    );
  }
}

export default withTranslation()(RefinateFiltersForm);
